/**
 * Renders one backoffice file by type, all in-app:
 * - docx → the Gotenberg PDF rendering (pdf.js), falling back to the
 *   OOXML DocxContent renderer when conversion is unavailable;
 * - pdf → pdf.js directly on the presigned bytes (an iframe would obey
 *   the object's attachment disposition and download instead);
 * - csv → a parsed table (capped), monospace fallback for odd shapes;
 * - images inline; anything else as a download link.
 */

import { useEffect, useMemo, useState } from 'react';

import { Button, Center, Loader, ScrollArea, Stack, Table, Text } from '@mantine/core';

import { DocxContent } from '@/components/Containers/DocxContent';
import { PdfViewer } from '@/components/Containers/PdfViewer';
import { vnextApiClient } from '@/services/vnext-api';
import type { BackofficeFile, BackofficeFileType } from '@/types/report';
import { parseCsv } from './csv';

export const FILE_TYPE_COLORS: Record<BackofficeFileType, string> = {
  docx: 'blue',
  pdf: 'red',
  csv: 'teal',
  xlsx: 'lime',
  image: 'green',
  other: 'gray',
};

const CSV_PREVIEW_MAX_ROWS = 500;

interface FilePreviewProps {
  file: BackofficeFile;
  /** Needed for the docx → PDF preview endpoint. */
  reportId: string;
}

export function FilePreview({ file, reportId }: Readonly<FilePreviewProps>) {
  if (file.file_type === 'docx') {
    return <DocxPdfPreview file={file} reportId={reportId} />;
  }
  if (file.file_type === 'pdf') {
    return (
      <ScrollArea style={{ height: '100%' }}>
        <PdfViewer pdfUrl={file.presigned_url} />
      </ScrollArea>
    );
  }
  if (file.file_type === 'csv') {
    return <CsvPreview file={file} />;
  }
  if (file.file_type === 'image') {
    return (
      <Center style={{ height: '100%' }}>
        <img
          src={file.presigned_url}
          alt={file.filename}
          style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
        />
      </Center>
    );
  }
  return (
    <Center style={{ height: '100%' }}>
      <Stack align="center" gap="sm">
        <Text c="dimmed">{file.filename}</Text>
        <Button component="a" href={file.presigned_url} target="_blank" rel="noreferrer" size="sm">
          Download file
        </Button>
      </Stack>
    </Center>
  );
}

/** docx previews as the Gotenberg-rendered PDF (what the client-facing
 * Presentation tab shows); the OOXML renderer is the fallback when the
 * converter is unconfigured or the render fails. */
function DocxPdfPreview({ file, reportId }: Readonly<FilePreviewProps>) {
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [fallback, setFallback] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setPdfUrl(null);
    setFallback(false);
    vnextApiClient
      .getBackofficeFilePdfPreview(reportId, file.raw_data_key)
      .then(({ url }) => {
        if (!cancelled) {
          setPdfUrl(url);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setFallback(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [reportId, file.raw_data_key]);

  if (fallback) {
    return <DocxContent docxUrl={file.presigned_url} />;
  }
  if (!pdfUrl) {
    return (
      <Center py="xl">
        <Loader size="sm" />
      </Center>
    );
  }
  return (
    <ScrollArea style={{ height: '100%' }}>
      <PdfViewer pdfUrl={pdfUrl} />
    </ScrollArea>
  );
}

function CsvPreview({ file }: Readonly<{ file: BackofficeFile }>) {
  const [text, setText] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setText(null);
    setError(null);
    fetch(file.presigned_url)
      .then(async (res) => {
        if (!res.ok) {
          throw new Error(`Failed to fetch CSV (${res.status})`);
        }
        return res.text();
      })
      .then((body) => {
        if (!cancelled) {
          setText(body);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load CSV');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [file.presigned_url]);

  const rows = useMemo(() => (text === null ? null : parseCsv(text)), [text]);

  if (error) {
    return (
      <Center py="xl">
        <Text c="red" size="sm">
          {error}
        </Text>
      </Center>
    );
  }
  if (rows === null) {
    return (
      <Center py="xl">
        <Loader size="sm" />
      </Center>
    );
  }
  if (rows.length === 0) {
    return (
      <Center py="xl">
        <Text c="dimmed" size="sm">
          This CSV is empty.
        </Text>
      </Center>
    );
  }

  const [header, ...body] = rows;
  const shown = body.slice(0, CSV_PREVIEW_MAX_ROWS);
  return (
    <ScrollArea style={{ height: '100%' }} data-testid="csv-preview">
      <Table striped highlightOnHover withTableBorder stickyHeader fz="xs">
        <Table.Thead>
          <Table.Tr>
            {header.map((cell, i) => (
              // Positional cells — a CSV has no stable per-cell identity.
              <Table.Th key={i}>{cell}</Table.Th>
            ))}
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {shown.map((row, r) => (
            <Table.Tr key={r}>
              {row.map((cell, c) => (
                <Table.Td key={c}>{cell}</Table.Td>
              ))}
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
      {body.length > CSV_PREVIEW_MAX_ROWS && (
        <Text size="xs" c="dimmed" p="xs">
          Showing the first {CSV_PREVIEW_MAX_ROWS.toLocaleString('en-GB')} of{' '}
          {body.length.toLocaleString('en-GB')} rows — download the file for the full data.
        </Text>
      )}
    </ScrollArea>
  );
}
